/**
 * Regulatory Explainer Component
 * Educational content explaining SSP regulations and 2026 changes
 */

import React, { useState } from 'react';
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Info,
  Calendar,
  Clock,
  PoundSterling,
  Users,
  Link as LinkIcon,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
} from 'lucide-react';
import { formatCurrency } from '../utils/sspCalculations';
import { SSP_WEEKLY_RATE, SSP_WEEKLY_RATE_2026, LOWER_EARNINGS_LIMIT } from '../data/constants';

export default function RegulatoryExplainer() {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'what-is-ssp',
      title: 'What is Statutory Sick Pay?',
      icon: Info,
      content: (
        <div className="space-y-3 text-sm text-slate-700">
          <p className="leading-relaxed">
            <strong>Statutory Sick Pay (SSP)</strong> is the legal minimum an employer must pay an employee who is too ill to work.
            It is paid by the employer (not the government) for up to 28 weeks.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-900 font-medium mb-2">Key Facts:</p>
            <ul className="space-y-1 text-blue-800">
              <li>• Employer-funded (not government benefit)</li>
              <li>• Maximum 28 weeks per period of incapacity</li>
              <li>• Paid at a flat weekly rate or percentage of earnings</li>
              <li>• Subject to eligibility criteria</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'piw',
      title: 'Period of Incapacity for Work (PIW)',
      icon: Calendar,
      content: (
        <div className="space-y-3 text-sm text-slate-700">
          <p className="leading-relaxed">
            A <strong>Period of Incapacity for Work (PIW)</strong> is a period of sickness lasting 4 or more consecutive days.
            All days count - including weekends, bank holidays, and non-working days.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <p className="font-medium text-red-900">No PIW Formed</p>
              </div>
              <p className="text-red-700 text-xs">1-3 days sick = No SSP payable</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="font-medium text-green-900">PIW Formed</p>
              </div>
              <p className="text-green-700 text-xs">4+ days sick = SSP may be payable</p>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-900 font-medium mb-2">⚠️ April 2026 Change:</p>
            <p className="text-amber-800">
              The PIW requirement remains - employees still must be sick for 4+ consecutive days to qualify for SSP.
              However, payment starts from day 1 (no waiting period).
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'qualifying-days',
      title: 'Qualifying Days',
      icon: Calendar,
      content: (
        <div className="space-y-3 text-sm text-slate-700">
          <p className="leading-relaxed">
            <strong>Qualifying days</strong> are the only days you can pay SSP for or count as waiting days.
            They are typically the days an employee normally works.
          </p>
          <p className="leading-relaxed">
            For shift workers with variable patterns, qualifying days should be agreed with the employee.
            If no agreement exists, every day of the week can be treated as a qualifying day.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-slate-900 font-medium mb-3">Daily SSP Rate Formula:</p>
            <p className="text-slate-700 font-mono text-center bg-white p-3 rounded border border-slate-300">
              Daily Rate = Weekly Rate ÷ Number of Qualifying Days
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>Qualifying Days</th>
                  <th className="text-right">2025/26 Rate</th>
                  <th className="text-right">2026/27 Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>7 days</td><td className="text-right">£16.96</td><td className="text-right">£17.61</td></tr>
                <tr><td>6 days</td><td className="text-right">£19.79</td><td className="text-right">£20.54</td></tr>
                <tr><td>5 days</td><td className="text-right">£23.75</td><td className="text-right">£24.65</td></tr>
                <tr><td>4 days</td><td className="text-right">£29.69</td><td className="text-right">£30.81</td></tr>
                <tr><td>3 days</td><td className="text-right">£39.58</td><td className="text-right">£41.08</td></tr>
                <tr><td>2 days</td><td className="text-right">£59.38</td><td className="text-right">£61.63</td></tr>
                <tr><td>1 day</td><td className="text-right">£118.75</td><td className="text-right">£123.25</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    {
      id: 'waiting-days',
      title: 'Waiting Days',
      icon: Clock,
      content: (
        <div className="space-y-3 text-sm text-slate-700">
          <p className="leading-relaxed">
            Under <strong>current rules</strong>, the first 3 qualifying days of a PIW are "waiting days" - they are unpaid.
            SSP only starts from the 4th qualifying day.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <p className="font-medium text-primary-900 mb-2">Current Rules (2025/26)</p>
              <div className="space-y-2 text-primary-800">
                <p>📅 Employee sick Monday-Friday (5 days)</p>
                <p className="ml-4">• Days 1-3 (Mon-Wed): <span className="font-bold text-red-600">£0</span> (waiting days)</p>
                <p className="ml-4">• Days 4-5 (Thu-Fri): <span className="font-bold text-green-600">SSP payable</span></p>
                <p className="font-mono text-xs mt-2">Result: 2 days × £23.75 = £47.50</p>
              </div>
            </div>
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
              <p className="font-medium text-accent-900 mb-2">April 2026 Rules</p>
              <div className="space-y-2 text-accent-800">
                <p>📅 Employee sick Monday-Friday (5 days)</p>
                <p className="ml-4">• All 5 days: <span className="font-bold text-green-600">SSP payable</span></p>
                <p className="ml-4 text-xs">(No waiting days)</p>
                <p className="font-mono text-xs mt-2">Result: 5 days × £24.65 = £123.25</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-900 font-medium mb-2">✓ Key Change from April 2026:</p>
            <p className="text-green-800">
              Waiting days are <strong>abolished</strong>. SSP is payable from the first qualifying day of sickness.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'awe',
      title: 'Average Weekly Earnings (AWE)',
      icon: PoundSterling,
      content: (
        <div className="space-y-3 text-sm text-slate-700">
          <p className="leading-relaxed">
            <strong>Average Weekly Earnings (AWE)</strong> determines both eligibility (under current rules) and
            the payment rate (for low earners from 2026). It is calculated using earnings from the 8 weeks ending
            on the last payday before sickness began.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-slate-900 font-medium mb-3">Calculation Methods:</p>
            <div className="space-y-2">
              <div>
                <p className="font-medium text-slate-800">Weekly Paid Employees:</p>
                <p className="font-mono text-xs bg-white p-2 rounded border border-slate-300 mt-1">
                  AWE = (Total earnings in 8-week period) ÷ 8
                </p>
              </div>
              <div>
                <p className="font-medium text-slate-800">Monthly Paid Employees:</p>
                <p className="font-mono text-xs bg-white p-2 rounded border border-slate-300 mt-1">
                  AWE = (Total earnings ÷ months) × 12 ÷ 52
                </p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-900 font-medium mb-2">Example:</p>
            <div className="text-blue-800 text-xs space-y-1">
              <p>Week 1-8 gross earnings: £200, £250, £180, £200, £220, £200, £190, £240</p>
              <p className="font-mono">Total: £1,680</p>
              <p className="font-mono">AWE: £1,680 ÷ 8 = <span className="font-bold">£210/week</span></p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'lel',
      title: 'Lower Earnings Limit (LEL)',
      icon: AlertCircle,
      content: (
        <div className="space-y-3 text-sm text-slate-700">
          <p className="leading-relaxed">
            Currently, employees must have AWE of at least <strong>{formatCurrency(LOWER_EARNINGS_LIMIT)}/week</strong> to qualify for SSP.
            Those earning below this get nothing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-medium text-red-900 mb-2">Current Rules (2025/26)</p>
              <div className="space-y-2 text-red-800 text-xs">
                <p>AWE £120/week &lt; LEL (£125)</p>
                <p className="font-bold text-lg">Result: £0</p>
                <p className="text-red-600">Not eligible for any SSP</p>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="font-medium text-green-900 mb-2">April 2026 Rules</p>
              <div className="space-y-2 text-green-800 text-xs">
                <p>AWE £120/week (LEL abolished)</p>
                <p className="font-bold text-lg">Result: £96/week</p>
                <p className="text-green-600">Receives 80% of AWE</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-900 font-medium mb-2">✓ From April 2026:</p>
            <p className="text-green-800 mb-2">
              The LEL is <strong>abolished</strong>. All employees qualify for SSP regardless of earnings.
            </p>
            <p className="text-green-800 text-xs">
              Those with AWE below £154.06/week will receive 80% of their AWE instead of the flat rate.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>AWE</th>
                  <th className="text-right">80% of AWE</th>
                  <th className="text-right">Rate Paid (2026)</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>£80</td><td className="text-right">£64.00</td><td className="text-right">£64.00</td></tr>
                <tr><td>£100</td><td className="text-right">£80.00</td><td className="text-right">£80.00</td></tr>
                <tr><td>£120</td><td className="text-right">£96.00</td><td className="text-right">£96.00</td></tr>
                <tr><td>£140</td><td className="text-right">£112.00</td><td className="text-right">£112.00</td></tr>
                <tr><td>£154.06</td><td className="text-right">£123.25</td><td className="text-right">£123.25</td></tr>
                <tr><td>£180</td><td className="text-right">£144.00</td><td className="text-right text-green-600 font-bold">£123.25 (capped)</td></tr>
                <tr><td>£200</td><td className="text-right">£160.00</td><td className="text-right text-green-600 font-bold">£123.25 (capped)</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    {
      id: 'linked-periods',
      title: 'Linked Periods',
      icon: LinkIcon,
      content: (
        <div className="space-y-3 text-sm text-slate-700">
          <p className="leading-relaxed">
            If an employee has multiple periods of sickness within <strong>8 weeks (56 days)</strong> of each other,
            they are "linked" and treated as one continuous PIW.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-900 font-medium mb-2">What this means:</p>
            <ul className="space-y-1 text-blue-800 text-sm">
              <li>✓ Waiting days are NOT served again in the linked period</li>
              <li>✓ Eligibility is based on conditions at the START of the first PIW</li>
              <li>✓ The 28-week maximum continues to count down</li>
            </ul>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <p className="text-slate-900 font-medium mb-2">Example:</p>
            <div className="text-slate-700 text-xs space-y-1">
              <p>• Employee sick Mon-Fri (5 days) in Week 1</p>
              <p>• Returns to work for Week 2-3</p>
              <p>• Sick again Mon-Wed (3 days) in Week 4</p>
              <p className="pt-2 border-t border-slate-300 mt-2 font-medium">
                Since Week 4 is within 8 weeks of Week 1, it's a linked period:
              </p>
              <p>✓ No waiting days in Week 4 (already served in Week 1)</p>
              <p>✓ All 3 days in Week 4 are payable</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'summary-changes',
      title: 'Summary: What Changes in April 2026',
      icon: TrendingUp,
      content: (
        <div className="space-y-4">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Aspect</th>
                  <th>Current Rules (2025/26)</th>
                  <th>April 2026 Rules</th>
                </tr>
              </thead>
              <tbody className="text-xs">
                <tr>
                  <td className="font-medium">Waiting Days</td>
                  <td>3 qualifying days unpaid</td>
                  <td className="text-green-700 font-medium">Abolished - SSP from day 1</td>
                </tr>
                <tr>
                  <td className="font-medium">Lower Earnings Limit</td>
                  <td>{formatCurrency(LOWER_EARNINGS_LIMIT)}/week minimum</td>
                  <td className="text-green-700 font-medium">Removed - all qualify</td>
                </tr>
                <tr>
                  <td className="font-medium">Low Earner Rate</td>
                  <td>Either full rate or nothing</td>
                  <td className="text-green-700 font-medium">80% of AWE, capped at flat rate</td>
                </tr>
                <tr>
                  <td className="font-medium">Flat Rate</td>
                  <td>{formatCurrency(SSP_WEEKLY_RATE)}/week</td>
                  <td className="text-green-700 font-medium">{formatCurrency(SSP_WEEKLY_RATE_2026)}/week</td>
                </tr>
                <tr>
                  <td className="font-medium">PIW Requirement</td>
                  <td>4+ consecutive days</td>
                  <td>Unchanged (4+ days)</td>
                </tr>
                <tr>
                  <td className="font-medium">28-week Maximum</td>
                  <td>Yes</td>
                  <td>Unchanged</td>
                </tr>
                <tr>
                  <td className="font-medium">Linked Periods</td>
                  <td>8 weeks (56 days)</td>
                  <td>Unchanged</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-8 h-8" />
          <h2 className="text-2xl font-bold">How SSP Works</h2>
        </div>
        <p className="text-blue-100">
          A comprehensive guide to UK Statutory Sick Pay regulations and the April 2026 Employment Rights Act changes
        </p>
      </div>

      {/* Expandable Sections */}
      <div className="space-y-3">
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.id;

          return (
            <div key={section.id} className="card">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full card-header flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-primary-600" />
                  <h3 className="font-semibold text-slate-900 text-left">{section.title}</h3>
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>

              {isExpanded && (
                <div className="card-body animate-fade-in">
                  {section.content}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Note */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <p className="text-xs text-slate-600 text-center">
          This information is based on GOV.UK guidance and the Employment Rights Act 2024.
          For official guidance, visit <a href="https://www.gov.uk/employers-sick-pay" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">www.gov.uk/employers-sick-pay</a>
        </p>
      </div>
    </div>
  );
}
